import React from 'react';
import { Stack, Group, MultiSelect, TextInput, Button, Collapse, Box, Text, Badge } from '@mantine/core';
import { Search, Filter, X } from 'lucide-react';
import { PipelineSection } from '../types/loan';
interface AdvancedFilterPanelProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedStages: string[];
  onStagesChange: (stages: string[]) => void;
  selectedBranches: string[];
  onBranchesChange: (branches: string[]) => void;
  selectedDivisions: string[];
  onDivisionsChange: (divisions: string[]) => void;
  branches: string[];
  divisions: string[];
  onClearAll: () => void;
  isOpen: boolean;
  onToggle: () => void;
}
const darkInputStyles = {
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.9)',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.3)'
    },
    '&:focus': {
      borderColor: 'rgba(59, 130, 246, 0.5)'
    }
  },
  label: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 600,
    marginBottom: '4px'
  },
  dropdown: {
    backgroundColor: '#1a2332',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  option: {
    color: 'rgba(255, 255, 255, 0.8)',
    '&[data-selected]': {
      backgroundColor: 'rgba(59, 130, 246, 0.2)'
    },
    '&[data-hovered]': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)'
    }
  },
  pill: {
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    color: '#60a5fa'
  }
};
export function AdvancedFilterPanel({
  searchQuery,
  onSearchChange,
  selectedStages,
  onStagesChange,
  selectedBranches,
  onBranchesChange,
  selectedDivisions,
  onDivisionsChange,
  branches,
  divisions,
  onClearAll,
  isOpen,
  onToggle
}: AdvancedFilterPanelProps) {
  const stages: PipelineSection[] = ['CTC', 'CD OUT', 'ALL IN', 'AWC', 'SUBMITTED'];
  const totalFilters = selectedStages.length + selectedBranches.length + selectedDivisions.length;
  return <Box style={{
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
  }} data-id="element-1">
      <Group justify="space-between" p="md" style={{
      cursor: 'pointer',
      borderBottom: isOpen ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
      backgroundColor: isOpen ? 'rgba(255, 255, 255, 0.03)' : 'transparent'
    }} onClick={onToggle} data-id="element-2">
        <Group gap="sm" data-id="element-3">
          <Box style={{
          padding: '6px',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          borderRadius: '6px'
        }} data-id="element-4">
            <Filter size={18} color="#60a5fa" data-id="element-5" />
          </Box>
          <Text fw={700} size="sm" style={{
          color: 'rgba(255, 255, 255, 0.9)'
        }} data-id="element-6">
            Advanced Filters
          </Text>
          {totalFilters > 0 && <Badge size="sm" variant="filled" color="blue" data-id="element-7">
              {totalFilters} active
            </Badge>}
        </Group>
        {totalFilters > 0 && <Button variant="subtle" size="xs" color="red" leftSection={<X size={14} data-id="element-9" />} onClick={e => {
        e.stopPropagation();
        onClearAll();
      }} styles={{
        root: {
          color: '#f87171',
          '&:hover': {
            backgroundColor: 'rgba(239, 68, 68, 0.1)'
          }
        }
      }} data-id="element-8">
            Clear All
          </Button>}
      </Group>

      <Collapse in={isOpen} data-id="element-10">
        <Stack gap="md" p="md" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.04)'
      }} data-id="element-11">
          <TextInput placeholder="Search by client, loan #, notes, lender..." leftSection={<Search size={16} color="rgba(255, 255, 255, 0.4)" data-id="element-13" />} value={searchQuery} onChange={e => onSearchChange(e.currentTarget.value)} size="sm" radius="md" styles={darkInputStyles} data-id="element-12" />

          <Group grow align="flex-start" data-id="element-14">
            <MultiSelect label="Pipeline Stage" placeholder="All stages" data={stages} value={selectedStages} onChange={onStagesChange} size="sm" radius="md" clearable searchable styles={darkInputStyles} data-id="element-15" />

            <MultiSelect label="Branch" placeholder="All branches" data={branches} value={selectedBranches} onChange={onBranchesChange} size="sm" radius="md" clearable searchable styles={darkInputStyles} data-id="element-16" />

            <MultiSelect label="Division" placeholder="All divisions" data={divisions} value={selectedDivisions} onChange={onDivisionsChange} size="sm" radius="md" clearable searchable styles={darkInputStyles} data-id="element-17" />
          </Group>
        </Stack>
      </Collapse>
    </Box>;
}